/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

'use strict';

function MediaAcceptProfilesEditView(
    this: JS5Object,
    _: JS5Object,
    $location: JS5Object,
    $q: JS5Object,
    $route: JS5Object,
    $routeParams: JS5Object,
    $timeout: JS5Function,
    BreadcrumbService: JS5Object,
    EnumService: JS5Object,
    MediaAcceptProfileService: JS5Object,
    MediaConversionTemplateService: JS5Object,
    ModalService: JS5Object,
    NotificationService: JS5Object,
    PermissionsService: JS5Object,
    SidebarService: JS5Object,
) {
    // Gets the data to populate dropdowns etc
    const getDataForFormControls = () => {
        this.loadingIndicator.show = true;
        MediaAcceptProfileService.getMediaAcceptProfileEditViewData().then((data: JS5Object) => {
            initializeFormControls(data)
        }).finally(() => {
            this.loadingIndicator.show = false;
        });
    };

    const getData = () => {
        _.set(this.enabledTabs, $location.search().editView, true);
        EnumService.queryForEnums().then(() => {
            if ($routeParams.id !== '-1') {
                getMapObject();
            } else {
                this.mapObject = _.cloneDeep(MediaAcceptProfileService.mapObject);
                getDataForFormControls();
            }
        });
    };

    const initializeFormControls = (data: JS5Object) => {
        this.macCaptionTemplates = _.cloneDeep(data.macCaptionTemplates);
        this.mediaConversionTemplates = _.cloneDeep(data.mediaConversionTemplates);
        this.repositoryRoots = _.cloneDeep(data.repositoryRoots);
        setAvailableVideoMasterTypes();
        setVideoMasterType();
        this.videoAcceptProfiles = _.filter(_.cloneDeep(data.acceptTemplates), {type: 'VIDEO'});
    };

    const setAvailableVideoMasterTypes = () => {
        //Setup Video Master Types for use in template
        if (_.get(this, 'mapObject.type') === 'VIDEO') {
            this.availableVideoMasterTypes = [];
            var tempVideoMasterTypes = EnumService.getEnumPicklist('MediaAcceptProfile_videoMasterType');
            _.forEach(tempVideoMasterTypes, (type: JS5Object) => {
                var typeObj = {type: type.id, displayName: type.displayName};
                this.availableVideoMasterTypes.push(typeObj);
            });
        }
    };

    //
    // The goal of this is to set the MAP's video master type to
    // an object with the properties "type" and "displayName".
    // This is accomplished by either looking it up by the MAP's
    // existing videoMasterType id which is the capitalized
    // underscore delimited string and then getting the
    // displayName and type or if in the other case where the MAP
    // does not have a video master type defined, by setting it to
    // the PICTURE_TO_PICTURE video master type.
    //
    // For example, if the MAP object's video master type is
    //
    //  {
    //      type: "'MASTER_VIDEO',
    //      programTimingRequired: true
    //  }
    //
    //  it will be set to
    //
    //  {
    //      displayName: 'Master Video',
    //      type: 'MASTER_VIDEO'
    //  }
    //
    //  In the case where the MAP object's video master type is
    //  undefined it will be set to
    //
    //  {
    //      displayName: 'Picture To Picture',
    //      type: 'PICTURE_TO_PICTURE'
    //  }
    //
    const setVideoMasterType = () => {
        let videoMasterTypeId = 'PICTURE_TO_PICTURE';
        if (_.has(this, 'mapObject.videoMasterType')) {
            videoMasterTypeId = this.mapObject.videoMasterType.type;
        }

        let videoMasterType = _
            .chain(EnumService.getEnumPicklist('MediaAcceptProfile_videoMasterType'))
            .find({id: videoMasterTypeId})
            .mapKeys((value: string, key: string) => {
                if (key === 'id') {
                    return 'type';
                } else {
                    return key;
                }
            })
            .value();
        _.set(this, 'mapObject.videoMasterType', videoMasterType);
    };

    // Get the MAP object
    const getMapObject = () => {
        this.loadingIndicator.show = true;
        $q.all({
            acceptProfile: MediaAcceptProfileService.getAcceptProfile($routeParams.id),
            formControlsData: MediaAcceptProfileService.getMediaAcceptProfileEditViewData()
        }).then((data: JS5Object) => {
            this.mapObject = _.cloneDeep(data.acceptProfile);
            MediaAcceptProfileService.originalAcceptProfile = _.cloneDeep(this.mapObject);
            initializeFormControls(data.formControlsData);
            //For breadcrumbs
            this.acceptProfileName = this.mapObject.name;

            setEnabledTabs(this.mapObject.type);
            this.minimumCaptionResult = this.mapObject.minimumCaptionDurationErrorFlag ? 'Error' : 'Warning';
        }).finally(() => {
            this.loadingIndicator.show = false;
        });
    };

    const resetTabsToPropertiesEnabledOnly = () => {
        this.enabledTabs = {
            properties: true,   // Properties tab is always enabled
            general: false,
            intermediates: false,
            sharedVideo: false,
            proxyPlayer: false,
            ccExtraction: false,
            clipExtraction: false,
            audio: false,
            video: false,
            audioChannels: false,
            image: false,
            artwork: false,
            text: false
        };
    };

    const setEnabledTabs = (type: string) => {
        resetTabsToPropertiesEnabledOnly();
        if ($routeParams.id !== '-1') {
            switch (type) {
                case 'VIDEO':
                    this.enabledTabs.general = true;
                    this.enabledTabs.video = true;
                    this.enabledTabs.audio = true;
                    this.enabledTabs.audioChannels = true;
                    this.enabledTabs.text = true;
                    this.enabledTabs.intermediates = true;
                    this.enabledTabs.sharedVideo = true;
                    this.enabledTabs.ccExtraction = true;
                    this.enabledTabs.clipExtraction = true;
                    this.enabledTabs.proxyPlayer = true;
                    break;

                case 'IMAGE':
                    this.enabledTabs.general = true;
                    this.enabledTabs.image = true;
                    break;

                case 'AUDIO':
                    this.enabledTabs.general = true;
                    this.enabledTabs.audio = true;
                    this.enabledTabs.audioChannels = true;
                    break;

                case 'OTHER':
                    this.enabledTabs.general = true;
                    this.enabledTabs.artwork = true;
                    break;

                case 'DOCUMENT':
                    this.enabledTabs.general = true;
                    break;

                default:
                    this.enabledTabs.properties = false;
                    console.warn('Unknown MAP type encountered when initializing MAP Edit View tabs.');
                    break;
            }
        }
    };

    const setToolbarButtonDefs = () => {
        if (this.currentTab === 'properties') {
            this.toolbarButtonDefs = allToolbarButtonDefs;
        } else {
            this.toolbarButtonDefs = _.reject(allToolbarButtonDefs, {label: 'Derive'});
        }
    };

    const saveMediaAcceptProfile = () => {
        this.loadingIndicator.show = true;
        if (_.get(this, 'mapObject.type') === 'ARTWORK') {
            this.mapObject.type = 'OTHER';
        }
        if (_.has(this.mapObject, 'sharedVideoDeliveryItem')) {
            _.set(this.mapObject, 'sharedVideoDeliveryItem.type', 'Video');
        }

        MediaAcceptProfileService.saveAcceptProfile(this.mapObject).then((savedProfile: JS5Object) => {
            this.editorForm.$setPristine();
            MediaAcceptProfileService.originalAcceptProfile = _.cloneDeep(savedProfile);
            $route.updateParams({id: savedProfile.id});
            this.minimumCaptionResult = savedProfile.minimumCaptionDurationErrorFlag ? 'Error' : 'Warning';
        }).finally(() => {
            this.loadingIndicator.show = false;
        });
    };


    //================//
    // Template Logic //
    //================//

    this.onClipMezzanineTypeChanged = (conversionType: string) => {
        this.clipMezzanineType = conversionType;
        if (conversionType && conversionType !== 'FFmpeg') {
            _.unset(this.mapObject, 'liveClipHLSConcatTemplate');
            _.unset(this.mapObject, 'liveClipIntermediateTemplate');
            _.unset(this.mapObject, 'ccExtractTemplate');
            _.unset(this.mapObject, 'liveClipsCCDelay');
        }
    };

    this.isClipMezzanineTemplateRequired = () => {
        return this.clipMezzanineType === 'Vantage';
    };

    this.onChildComponentSetFormPristineHandler = () => {
        this.editorForm.$setPristine();
    };

    this.numericFieldKeyDown = ($event: JS5Object, currentValue: string) => {
        if ($event.key.length === 1) {
            var newValue = currentValue || '';
            newValue += $event.key;
            if (!(/^([0-9]*|[0-9]*[.][0-9]*)$/.test(newValue))) {
                $event.preventDefault();
            }
        }
    };

    this.onTabDeselect = (tab: string, event: any) => {
        if (this.editorForm.$dirty) {
            NotificationService.createWarningNotification('You have unsaved changes.  Please save or revert.', '');
            event.preventDefault();
        } else {
            this.editorForm.$setPristine();
        }
    };

    this.onTabSelect = (tab: string) => {
        if (!this.editorForm.$dirty) {
            this.currentTab = tab;
            setToolbarButtonDefs();
            $location.search('editView', tab);
        }
    };

    this.onMinimumCaptionResultChanged = () => {
        this.mapObject.minimumCaptionDurationErrorFlag = _.toLower(this.minimumCaptionResult) === 'error';
    };

    const returnToMAPSearch = () => {
        $location.url(`/system/templates/search`).search({tab: 'Media', subTab: 'MediaAcceptProfiles'});
    };

    const allToolbarButtonDefs = [
        {
            label: 'Save',
            enabled: () => {
                // shared video delivery items require a filename
                // expression
                let sharedVideoDeliveryItemValid = true;
                if (_.get(this, 'mapObject.sharedVideoDeliveryItem.mediaConversionTemplate') &&
                    !_.size(_.get(this, 'mapObject.sharedVideoDeliveryItem.filenameExpression'))) {
                    sharedVideoDeliveryItemValid = false;
                }

                // clip mezzanines require a transcoder template
                let clipMezzanineTemplateValid = true;
                if (this.isClipMezzanineTemplateRequired() &&
                    !_.get(this, 'mapObject.videoExtractTemplate')) {
                    clipMezzanineTemplateValid = false;
                }

                let proxyTemplateValid = true;
                if (_.get(this, 'mapObject.type') === 'VIDEO' || _.get(this, 'mapObject.type') === 'AUDIO') {
                    proxyTemplateValid = !!_.get(this, 'mapObject.proxyTemplate');
                }

                // all MAPs require a name
                return !_.isEmpty(_.get(this, 'mapObject.name'))
                    && sharedVideoDeliveryItemValid
                    && clipMezzanineTemplateValid
                    && proxyTemplateValid
                    && _.get(this, 'editorForm.$dirty');
            },
            onClick: () => {
                saveMediaAcceptProfile()
            }
        },
        {
            label: 'Revert',
            enabled: () => {
                return this.editorForm && this.editorForm.$dirty;
            },
            onClick: () => {
                if ($routeParams.id !== '-1') {
                    this.mapObject = _.cloneDeep(MediaAcceptProfileService.originalAcceptProfile);
                    this.editorForm.$setPristine();
                } else {
                    returnToMAPSearch();
                }
            }
        },
        {
            label: 'Derive',
            icon: 'fa-upload',
            enabled: () => {
                return $routeParams.id !== '-1' && !_.get(this, 'editorForm.$dirty');
            },
            onClick: () => {
                ModalService.showGenerateAcceptProfileModal(this.mapObject.type, $routeParams.id);
            }
        },
        {
            label: 'Close',
            enabled: () => {
                return !_.get(this, 'editorForm.$dirty');
            },
            onClick: () => {
                returnToMAPSearch();
            }
        }
    ];


    //=================//
    // Lifecycle Hooks //
    //=================//

    const setBreadcrumb = (action: string) => {
        let name = `${action} Media Accept Profile`;

        BreadcrumbService.addBreadcrumb({
            name,
            location: '/system/templates/search',
            params: {
                tab: 'Media',
                subTab: 'MediaAcceptProfiles'
            }
        });
    };

    this.$onInit = () => {
        // Needed for now because dropdown-select doesn't like
        // boolean model-props
        this.minimumCaptionResultOptions = [ 'Warning', 'Error' ];
        this.minimumCaptionResult = 'Warning';

        this.clipMezzanineTypeTooltip = 'system/templates/mediaAcceptProfiles/editView/renderers/clipextraction-mezzanine-type-tooltip-template.html';
        this.isAdmin = PermissionsService.isAdmin();
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        resetTabsToPropertiesEnabledOnly();

        if (this.isAdmin) {
            this.enabledTabs[$location.search().editView] = true;
            this.currentTab = $location.search().editView;
            setToolbarButtonDefs();

            BreadcrumbService.clearBreadcrumbs();

            if ($routeParams.id !== '-1') {
                // get existing MAP
                if (!_.size(BreadcrumbService.getBreadcrumbs())) {
                    setBreadcrumb('Edit');
                }
                getData();
            } else {
                // create new MAP
                if (_.has(MediaAcceptProfileService.mapObject, 'type')) {
                    _.set(this.editData, 'mediaAcceptProfiles', []);
                    setEnabledTabs(_.get(MediaAcceptProfileService.mapObject, 'type'));

                    MediaAcceptProfileService.originalEditData = _.cloneDeep(this.editData);

                    setBreadcrumb('Create');
                    getData();
                } else {
                    $location.url(`/system/templates/search`).search({tab: 'Media', subTab: 'MediaAcceptProfiles'});
                }
            }
        } else {

            //
            // TODO
            //
            // Display a warning notification here and return to
            // the MAP search view
            //

            $location.url('/login');
        }
    };

    this.$postLink = () => {
        $timeout(() => {
            MediaAcceptProfileService.editorForm = this.editorForm;
            SidebarService.editService = MediaAcceptProfileService;
        });
    };

    this.$onDestroy = () => {
        MediaAcceptProfileService.editorForm = null;
    };
}

angular.module('prism-ui.mediaAcceptProfiles').component('mediaAcceptProfilesEditView', {
    templateUrl: 'system/templates/mediaAcceptProfiles/editView/media-accept-profiles-editview-template.html',
    controller: MediaAcceptProfilesEditView,
    bindings: {
    }
});
